import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, InjectionToken } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { IPublicClientApplication, PublicClientApplication, BrowserCacheLocation, LogLevel, InteractionType } from '@azure/msal-browser';
import { routes } from './app.routes';
import { ConfirmationService } from 'primeng/api';
import { TranslateHttpLoader, TRANSLATE_HTTP_LOADER_CONFIG } from '@ngx-translate/http-loader';
import { NgxsModule } from '@ngxs/store';
import { environment } from './core/environments/environment';
import { licenseKey } from './devextreme-license';
import config from 'devextreme/core/config';
import {
  CONDITIONAL_SERVICE_PROVIDER,
  ActionRequiredState,
  AppEnvironmentState,
  FeatureFlagConfigurationState,
  PermissionsState,
  UserState,
  LIBRARY_CONFIG,
  OfflineHeaderModule,
  DialogV2Module,
  provideSnackbarV3,
} from '@gea/digital-ui-lib';
import { BrowserModule } from '@angular/platform-browser';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { enghubapiInterceptor } from './shared/interceptors/enghubapi-interceptor';
import { ErrorInterceptor } from './shared/interceptors/error-interceptor';
import { provideMarkdown } from 'ngx-markdown';

config({
  licenseKey
});


export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig!.clientId,
      authority: environment.msalConfig!.authority,
      redirectUri: environment.msalConfig!.redirectUri,
      postLogoutRedirectUri: environment.msalConfig!.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    'https://graph.microsoft.com/v1.0/me',
    ['user.read']
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    loginFailedRoute: '/login-failed',
  };
}
export const APP_ENVIRONMENT = new InjectionToken('app.environment', {
  providedIn: 'root',
  factory: () => environment
});


export const LocalGeaStorageProvider = importProvidersFrom(
  NgxsModule.forRoot(
    [
      UserState,
      ActionRequiredState,
      AppEnvironmentState,
      PermissionsState,
      FeatureFlagConfigurationState,
    ],
    {
      compatibility: {
        strictContentSecurityPolicy: true,
      },
    }
  )
);
export function initializeMsal(msalInstance: IPublicClientApplication) {
  return async () => {
    await msalInstance.initialize();
    console.log('MSAL initialized.');
    await msalInstance.handleRedirectPromise();
  };
}
export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptors([enghubapiInterceptor, ErrorInterceptor])),
    // provideHttpClient(withInterceptors([debugInterceptor])),
    LocalGeaStorageProvider,
    ...provideSnackbarV3(),
    importProvidersFrom(
      AngularSvgIconModule.forRoot(),
      BrowserModule,
      DialogV2Module,
      DynamicDialogModule,
      OfflineHeaderModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: TranslateHttpLoader,
        }
      }),

    ),
    provideAnimations(),
    provideRouter(routes),
    provideMarkdown(),
    {
      provide: LIBRARY_CONFIG,
      useFactory: () => ({
        baseUrl: "http://localhost:4200/",
        legalDocumentsStorageURL: "http://localhost:4200/",
        iconStorageURL: '/assets/icons-v2/'
      }),
      // useFactory: (env: EnvironmentConfiguration) => {
      //   return {
      //     ...env,
      //     baseUrl: env.portal.baseURL ?? env.baseURL,
      //     backendInfoUrl: (env.portal.baseURL ?? env.baseURL) + 'geaid/v1/info',
      //     legalDocumentsStorageURL: env.storageURL,
      //   };
      // },
    },

    { provide: 'iconPath', useValue: '/assets/icons-v2/' },
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: { prefix: './assets/i18n/', suffix: '.json' },
    },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MSAL_INSTANCE],
      multi: true
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    CONDITIONAL_SERVICE_PROVIDER,
    TranslateService,
    ConfirmationService,
    DialogService,
  ],
};
